
import React, { useState, useMemo } from 'react';
import { X, Calendar, Clock, Info } from 'lucide-react';
import { Patient, CycleFrequency, Approach, Indicator } from '../types';
import { APPROACH_LIST, INDICATORS } from '../constants';
import { maskPhone } from '../utils/phone';

interface PatientEditModalProps {
  patient: Patient;
  onClose: () => void;
  onSave: (patient: Patient) => void;
  inline?: boolean;
  /**
   * Retorno do salvamento (bloco 28): exibido logo abaixo do botão
   * "Salvar Alterações", onde o usuário está olhando, e não no topo da página.
   */
  feedback?: { ok: boolean; msg: string } | null;
}

const PatientEditModal: React.FC<PatientEditModalProps> = ({ patient, onClose, onSave, inline = false, feedback = null }) => {
  const userProfiles = useMemo(() => {
    try {
      const saved = localStorage.getItem('allodos-user-profiles');
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    return [
      { id: '1', name: 'Dra. Marina Lopes', role: 'Administrador / Psicóloga', email: 'contato@clinicaevoluir.com', status: 'Ativo' },
      { id: '2', name: 'Ana Paula Silva', role: 'Recepcionista', email: 'recepcao@clinicaevoluir.com', status: 'Ativo' }
    ];
  }, []);

  const professionals = useMemo(() => {
    return userProfiles.filter((p: any) => {
      const roleLower = (p.role || '').toLowerCase();
      return roleLower.includes('administrador') || roleLower.includes('profissional') || roleLower.includes('psicólog');
    });
  }, [userProfiles]);

  const { activeProfile, isProfessional } = useMemo(() => {
    try {
      const saved = localStorage.getItem('allodos-user-profiles');
      const activeId = localStorage.getItem('allodos-active-profile-id') || '1';
      if (saved) {
        const list = JSON.parse(saved);
        const profile = list.find((p: any) => p.id === activeId) || list[0];
        const roleLower = (profile?.role || '').toLowerCase();
        const isProf = (roleLower.includes('profissional') || roleLower.includes('psicólog') || roleLower.includes('terapeut')) && !roleLower.includes('administrador');
        return { activeProfile: profile, isProfessional: isProf };
      }
    } catch (e) {}
    return { 
      activeProfile: { id: '1', name: 'Dra. Marina Lopes', role: 'Administrador', email: 'contato@clinicaevoluir.com', status: 'Ativo' }, 
      isProfessional: false 
    };
  }, []);

  // Bloco 34: a recepção só edita os Dados Cadastrais.
  const isReceptionist = useMemo(() => {
    const roleLower = String((activeProfile as any)?.role || '').toLowerCase();
    return roleLower.includes('recep') || roleLower.includes('secretar');
  }, [activeProfile]);

  const [formData, setFormData] = useState({ 
    ...patient,
    assignedProfessionalId: isProfessional ? activeProfile.id : (patient.assignedProfessionalId || professionals[0]?.id || '1'),
    assignedProfessionalName: isProfessional ? activeProfile.name : (patient.assignedProfessionalName || professionals[0]?.name || 'Dra. Marina Lopes'),
    medications: Array.isArray(patient.medications) 
      ? patient.medications 
      : (typeof patient.medications === 'string' && patient.medications.trim() !== '' 
          ? [{ name: patient.medications, quantity: '', time: '' }] 
          : [])
  });
  const [hoveredIndicator, setHoveredIndicator] = useState<string | null>(null);

  const groupedIndicators = useMemo(() => {
    return INDICATORS.reduce((acc, ind) => {
      if (!acc[ind.dominio]) acc[ind.dominio] = [];
      acc[ind.dominio].push(ind);
      return acc;
    }, {} as Record<string, Indicator[]>);
  }, []);

  const toggleIndicator = (id: string) => {
    setFormData(prev => ({
      ...prev,
      selectedIndicatorIds: prev.selectedIndicatorIds.includes(id)
        ? prev.selectedIndicatorIds.filter(i => i !== id)
        : [...prev.selectedIndicatorIds, id]
    }));
  };

  const calculateAge = (dob: string) => {
    if (!dob) return '';
    const birthDate = new Date(dob);
    if (isNaN(birthDate.getTime())) return '';
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age.toString();
  };

  const handleMedicationChange = (index: number, field: 'name' | 'quantity' | 'time', value: string) => {
    const newMedications = [...formData.medications];
    newMedications[index] = { ...newMedications[index], [field]: value };
    setFormData(prev => ({ ...prev, medications: newMedications }));
  };

  const addMedication = () => {
    setFormData(prev => ({
      ...prev,
      medications: [...(prev.medications || []), { name: '', quantity: '', time: '' }]
    }));
  };

  const removeMedication = (index: number) => {
    setFormData(prev => ({
      ...prev,
      medications: (prev.medications || []).filter((_, i) => i !== index)
    }));
  };

  // Bloco #47: o cadastro salva sem indicador (paciente vindo do link externo
  // não tem nenhum, e o botão ficava desabilitado sem aviso). Só bloqueia quem
  // já tinha indicadores e desmarcou todos — regra da API.
  const removingAllIndicators =
    (patient.selectedIndicatorIds?.length ?? 0) > 0 && (formData.selectedIndicatorIds?.length ?? 0) === 0;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'dob') {
      const calculatedAge = calculateAge(value);
      setFormData(prev => ({ 
        ...prev, 
        [name]: value,
        age: calculatedAge 
      }));
    } else if (name === 'phone' || name === 'emergencyPhone') {
      // Blocos #47/#48: máscara padrão "(11) 99999-9999", sem perder dígitos.
      setFormData(prev => ({ ...prev, [name]: maskPhone(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCepChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const cleanCep = rawValue.replace(/\D/g, '').slice(0, 8);
    
    let formattedCep = cleanCep;
    if (cleanCep.length > 5) {
      formattedCep = `${cleanCep.slice(0, 5)}-${cleanCep.slice(5)}`;
    }
    
    setFormData(prev => ({ ...prev, cep: formattedCep }));

    if (cleanCep.length === 8) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
        const data = await response.json();
        if (!data.erro) {
          const addressString = `${data.logradouro}${data.bairro ? `, ${data.bairro}` : ''}, ${data.localidade} - ${data.uf}`;
          setFormData(prev => ({
            ...prev,
            address: addressString
          }));
        }
      } catch (error) {
        console.error('Erro ao buscar o CEP:', error);
      }
    }
  };

  const inputClasses = "w-full p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-400 transition-all text-sm text-slate-700 dark:text-slate-200 placeholder:text-slate-300 dark:placeholder:text-slate-600 shadow-sm";
  const labelClasses = "text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.15em] mb-1.5 block ml-1";

  const renderInner = () => (
    <>
      <div className={`${inline ? 'p-2' : 'flex-1 overflow-y-auto p-4 sm:p-8'} flex flex-col gap-6 sm:gap-12`}>
          <div className="space-y-8">
            <h4 className="text-[10px] font-black text-indigo-500 dark:text-indigo-400 uppercase tracking-[0.2em] border-b border-indigo-100 dark:border-indigo-900/40 pb-2">Dados Cadastrais</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="col-span-full space-y-1.5">
                <label className={labelClasses}>Nome Completo do Paciente</label>
                <input name="name" value={formData.name} onChange={handleChange} className={inputClasses} placeholder="Ex: João da Silva" />
              </div>

              <div className="space-y-1.5">
                <label className={labelClasses}>Data de Nascimento</label>
                <div className="relative">
                  <input type="date" name="dob" value={formData.dob} onChange={handleChange} className={`${inputClasses} pl-10`} />
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" size={16} />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className={labelClasses}>Idade Calculada</label>
                <input name="age" value={formData.age} onChange={handleChange} className={`${inputClasses} bg-slate-50 dark:bg-slate-900 font-bold`} placeholder="Calculada automaticamente" readOnly />
              </div>

              <div className="space-y-1.5">
                <label className={labelClasses}>Telefone</label>
                <input type="tel" inputMode="tel" name="phone" value={formData.phone || ''} onChange={handleChange} className={inputClasses} placeholder="(00) 00000-0000" />
              </div>
              <div className="space-y-1.5">
                <label className={labelClasses}>E-mail</label>
                <input name="email" value={formData.email || ''} onChange={handleChange} className={inputClasses} placeholder="paciente@exemplo.com" />
              </div>

              <div className="space-y-1.5">
                <label className={labelClasses}>CPF</label>
                <input name="cpf" value={formData.cpf || ''} onChange={handleChange} className={inputClasses} placeholder="Ex: 000.000.000-00" />
              </div>
              <div className="space-y-1.5">
                <label className={labelClasses}>Telefone de Emergência / Responsável</label>
                <input type="tel" inputMode="tel" name="emergencyPhone" value={formData.emergencyPhone || ''} onChange={handleChange} className={inputClasses} placeholder="Ex: (11) 98888-8888" />
              </div>

              <div className="col-span-full space-y-1.5">
                <label className={labelClasses}>Contato de Emergência (Nome e Vínculo)</label>
                <input name="emergencyContact" value={formData.emergencyContact || ''} onChange={handleChange} className={inputClasses} placeholder="Nome e Vínculo" />
              </div>

              <div className="col-span-full grid grid-cols-1 sm:grid-cols-4 gap-5">
                <div className="sm:col-span-1 space-y-1.5">
                  <label className={labelClasses}>CEP</label>
                  <input 
                    name="cep" 
                    value={formData.cep || ''} 
                    onChange={handleCepChange} 
                    className={inputClasses} 
                    placeholder="Ex: 01310-100" 
                    maxLength={9}
                  />
                </div>
                <div className="sm:col-span-3 space-y-1.5">
                  <label className={labelClasses}>Endereço Completo</label>
                  <input 
                    name="address" 
                    value={formData.address || ''} 
                    onChange={handleChange} 
                    className={inputClasses} 
                    placeholder="Rua, Número, Bairro, Cidade - UF" 
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className={labelClasses}>Nome do Pai</label>
                <input name="fatherName" value={formData.fatherName || ''} onChange={handleChange} className={inputClasses} placeholder="Nome do Pai" />
              </div>
              <div className="space-y-1.5">
                <label className={labelClasses}>Nome da Mãe</label>
                <input name="motherName" value={formData.motherName || ''} onChange={handleChange} className={inputClasses} placeholder="Nome da Mãe" />
              </div>
            </div>
          </div>

          {/* Bloco 34: a recepção vê, mas não altera, os dados clínicos. */}
          <fieldset disabled={isReceptionist} className="space-y-8 border-0 p-0 m-0 min-w-0 disabled:opacity-80 disabled:cursor-not-allowed">
            <h4 className="text-[10px] font-black text-indigo-500 dark:text-indigo-400 uppercase tracking-[0.2em] border-b border-indigo-100 dark:border-indigo-900/40 pb-2 flex items-center justify-between gap-3">
              <span>Domínios de Acompanhamento Clínico</span>
              {isReceptionist && (
                <span className="text-[9px] font-black uppercase tracking-widest text-slate-400 bg-slate-100 dark:bg-slate-800 px-3 py-1 rounded-lg normal-case">
                  Somente leitura · dados clínicos são do profissional/administrador
                </span>
              )}
            </h4>
            
            <div className="space-y-6 bg-slate-50 dark:bg-slate-800/30 p-5 rounded-2xl border border-slate-200/50 dark:border-slate-800/50">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="space-y-1.5">
                  <label className={labelClasses}>Abordagem Terapêutica</label>
                  <div className="relative">
                    <select name="activeApproach" value={formData.activeApproach} onChange={handleChange} className={`${inputClasses} h-[46px] py-0 font-bold text-slate-700 dark:text-slate-300`}>
                      {APPROACH_LIST.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className={labelClasses}>Periodicidade do Ciclo</label>
                  <div className="relative">
                    <select name="cycleFrequency" value={formData.cycleFrequency} onChange={handleChange} className={`${inputClasses} h-[46px] py-0 pl-10 appearance-none font-bold text-indigo-700 dark:text-indigo-400`}>
                      {Object.values(CycleFrequency).map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 text-indigo-400" size={16} />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className={labelClasses}>Profissional Responsável</label>
                  <div className="relative">
                    {!isProfessional ? (
                      <select 
                        name="assignedProfessionalId" 
                        value={formData.assignedProfessionalId} 
                        onChange={(e) => {
                          const selectedId = e.target.value;
                          const prof = professionals.find(p => p.id === selectedId);
                          setFormData(prev => ({
                            ...prev,
                            assignedProfessionalId: selectedId,
                            assignedProfessionalName: prof ? prof.name : ''
                          }));
                        }} 
                        className={`${inputClasses} h-[46px] py-0 font-bold text-indigo-700 dark:text-indigo-400`}
                      >
                        {professionals.map(p => (
                          <option key={p.id} value={p.id}>{p.name} ({p.role})</option>
                        ))}
                      </select>
                    ) : (
                      <div className={`${inputClasses} h-[46px] flex items-center bg-slate-100 dark:bg-slate-800/85 text-slate-500 font-bold text-xs px-4 rounded-xl`}>
                        {formData.assignedProfessionalName}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="border-t border-slate-200/50 dark:border-slate-700/30"></div>

              <div className="space-y-1.5">
                <label className={labelClasses}>Queixa Principal</label>
                <textarea name="mainComplaint" value={formData.mainComplaint || ''} onChange={handleChange} className={`${inputClasses} min-h-[100px] resize-none leading-relaxed bg-white dark:bg-slate-900`} placeholder="Breve descrição do motivo da busca por terapia..." />
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <label className={labelClasses}>Medicamentos em Uso</label>
                  <button 
                    type="button"
                    onClick={addMedication}
                    className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest hover:text-indigo-700 transition-colors cursor-pointer"
                  >
                    + Adicionar
                  </button>
                </div>
                
                {formData.medications && formData.medications.length > 0 ? (
                  <div className="overflow-hidden border border-slate-200 dark:border-slate-800 rounded-2xl bg-white dark:bg-slate-900">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-slate-50 dark:bg-slate-800/50 border-b dark:border-slate-800">
                          <th className="p-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Medicamento</th>
                          <th className="p-3 text-[10px] font-black text-slate-400 uppercase tracking-widest w-24">Qtd</th>
                          <th className="p-3 text-[10px] font-black text-slate-400 uppercase tracking-widest w-32">Horário</th>
                          <th className="p-3 w-10"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y dark:divide-slate-800">
                        {formData.medications.map((med, index) => (
                          <tr key={index} className="bg-white dark:bg-slate-900">
                            <td className="p-2">
                              <input 
                                value={med.name}
                                onChange={(e) => handleMedicationChange(index, 'name', e.target.value)}
                                className="w-full p-2 bg-transparent focus:outline-none text-sm dark:text-slate-200 font-medium"
                                placeholder="Nome..."
                              />
                            </td>
                            <td className="p-2 border-l dark:border-slate-800">
                              <input 
                                value={med.quantity}
                                onChange={(e) => handleMedicationChange(index, 'quantity', e.target.value)}
                                className="w-full p-2 bg-transparent focus:outline-none text-sm dark:text-slate-200 font-medium"
                                placeholder="Ex: 1cp"
                              />
                            </td>
                            <td className="p-2 border-l dark:border-slate-800">
                              <input 
                                value={med.time}
                                onChange={(e) => handleMedicationChange(index, 'time', e.target.value)}
                                className="w-full p-2 bg-transparent focus:outline-none text-sm dark:text-slate-200 font-medium"
                                placeholder="Ex: 08:00"
                              />
                            </td>
                            <td className="p-2 text-center">
                              <button 
                                type="button"
                                onClick={() => removeMedication(index)}
                                className="text-rose-400 hover:text-rose-600 transition-colors cursor-pointer"
                              >
                                <X size={14} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-4 border border-dashed border-slate-200 dark:border-slate-800 rounded-2xl text-center bg-white dark:bg-slate-900">
                    <p className="text-xs text-slate-400 font-bold uppercase tracking-widest">Nenhum medicamento registrado</p>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-6">
              {(Object.entries(groupedIndicators) as [string, Indicator[]][]).map(([domain, items]) => (
                <div key={domain} className="space-y-3">
                  <h5 className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.3em] flex items-center gap-2">
                    <div className="w-1 h-3 bg-indigo-500 rounded-full"></div>
                    {domain}
                  </h5>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {items.map(ind => (
                      <div 
                        key={ind.id} 
                        className="relative"
                        onMouseEnter={() => setHoveredIndicator(ind.id)}
                        onMouseLeave={() => setHoveredIndicator(null)}
                      >
                        <button
                          onClick={() => toggleIndicator(ind.id)}
                          className={`w-full p-3.5 rounded-2xl border text-left transition-all relative group flex items-center justify-between ${
                            formData.selectedIndicatorIds.includes(ind.id) 
                              ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-900/20 shadow-sm' 
                              : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-800 hover:border-indigo-200'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-2 h-2 rounded-full ${ind.eixo === 'positive' ? 'bg-emerald-400' : 'bg-rose-400'}`}></div>
                            <span className={`text-[11px] font-black uppercase tracking-widest ${formData.selectedIndicatorIds.includes(ind.id) ? 'text-indigo-700 dark:text-indigo-400' : 'text-slate-600 dark:text-slate-400'}`}>
                              {ind.nome}
                            </span>
                          </div>
                          <Info size={12} className={`${formData.selectedIndicatorIds.includes(ind.id) ? 'text-indigo-400' : 'text-slate-300 dark:text-slate-600'}`} />
                        </button>

                        {hoveredIndicator === ind.id && (
                          <div className="absolute z-20 left-0 right-0 -top-2 -translate-y-full bg-slate-900 text-white p-3 rounded-xl text-[10px] shadow-xl animate-in fade-in zoom-in-95 pointer-events-none border border-slate-700">
                            <div className="font-black mb-1 text-indigo-300 dark:text-indigo-400 uppercase tracking-widest">{ind.nome}</div>
                            <p className="leading-relaxed opacity-90">{ind.descricao_clinica}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </fieldset>
      </div>

      <div className="p-5 sm:p-8 border-t dark:border-slate-800 bg-slate-50/80 dark:bg-slate-800/50 flex flex-col gap-4">
       {removingAllIndicators && (
        <p role="alert" className="px-5 py-3 rounded-2xl text-xs font-bold bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300 border border-amber-100 dark:border-amber-900/50">
          Mantenha ao menos um indicador selecionado para salvar.
        </p>
       )}
       <div className="flex gap-4">
        {!inline && (
          <button onClick={onClose} className="flex-1 p-4 border border-rose-200 dark:border-rose-900/30 bg-rose-50 dark:bg-rose-950/20 rounded-2xl font-black text-rose-500 dark:text-rose-400 hover:bg-rose-100 dark:hover:bg-rose-900/40 transition-all shadow-sm uppercase text-xs tracking-widest leading-none">Descartar</button>
        )}
        <button 
          onClick={() => onSave(formData)} 
          disabled={!formData.name || removingAllIndicators}
          className={`${inline ? 'w-full' : 'flex-1'} p-4 bg-emerald-600 text-white rounded-2xl font-black hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-100 dark:shadow-none disabled:opacity-50 disabled:cursor-not-allowed uppercase text-xs tracking-widest leading-none`}
        >
          {inline ? "Salvar Alterações" : "Atualizar Registro"}
        </button>
       </div>
        {feedback && (
          <div
            role="status"
            aria-live="polite"
            className={`px-5 py-4 rounded-2xl text-xs font-bold flex items-center gap-2 animate-in fade-in slide-in-from-top-1 ${
              feedback.ok
                ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 border border-emerald-100 dark:border-emerald-900/50'
                : 'bg-rose-50 dark:bg-rose-950/30 text-rose-700 dark:text-rose-300 border border-rose-100 dark:border-rose-900/50'
            }`}
          >
            {feedback.msg}
          </div>
        )}
      </div>
    </>
  );

  if (inline) {
    return (
      <div className="w-full flex flex-col bg-white dark:bg-slate-900">
        {renderInner()}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl animate-in fade-in zoom-in-95 duration-200 border dark:border-slate-800">
        <div className="p-5 sm:p-8 border-b dark:border-slate-800 flex justify-between items-center bg-slate-50/80 dark:bg-slate-800/50">
          <div>
            <h3 className="text-2xl font-black text-slate-800 dark:text-slate-100">Editar Paciente</h3>
            <p className="text-xs text-slate-400 dark:text-slate-500 font-bold uppercase tracking-widest mt-1">ID: #{formData.id.slice(-4)}</p>
          </div>
          <button onClick={onClose} className="p-3 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-2xl transition-colors"><X size={24} className="text-slate-500 dark:text-slate-400" /></button>
        </div>
        {renderInner()}
      </div>
    </div>
  );
};

export default PatientEditModal;
